import { defineStore } from 'pinia';
import API from "@/shared/helpers/API";
import {useUserStore} from "@/user/stores/user.store";
import {getLocalDateFromServer} from "@/shared/helpers/dates";

export const useSuperStore = defineStore('super', {
	state: () => ({
		businesses: null,
		isSuper: false,
		isUltra: false,
		isMega: false,
		minFeePerAllowed: null,
	}),
	getters: {
		// No user-level restrictions - plan-based floors are enforced by backend
	},
	actions: {
		fetchBusinesses(callback = null) {
			API.init().superStatsBusinesses(businessesData => {
				this.businesses = [];
				if(businessesData && businessesData.businesses && businessesData.users) {
					let bObj = {};
					let addresses = {};
					businessesData.addresses.forEach(address => {
						addresses[address.address_id] = address;
					});
					businessesData.businesses.forEach(business => {
						business.active = false;
						business.customSlot = 'business';
						business.headerSlot = 'businessHeader';
						business.dropdownSlot = 'businessDropdown';
						business.deleted = business.deleted === 1;
						business.title = business.name;
						business.payout_interval = business.payout_interval || null;
						business.payout_delay = business.payout_delay ? Number(business.payout_delay) : null;
						business.verified_user_id = business.verified_user_id ? Number(business.verified_user_id) : null;
						business.openBalance = Number(business.open_balance);
						business.repayment_rate = Number(business.repayment_rate);
						business.plan_plugin_order_pay = Number(business.plan_plugin_order_pay) === 1;
						business.plan_plugin_pos = Number(business.plan_plugin_pos) === 1;
						business.plan_plugin_counter = Number(business.plan_plugin_counter) === 1;
						business.stripe_account = business.stripe_account || null;
						business.transfer_prefix = business.transfer_prefix || null;
						business.allow_tap_to_pay = business.allow_tap_to_pay === 1;
						business.crm_deal_id = business.crm_deal_id || null;
						business.users = {};
						business.feerules = {};
						if(business.address_id && addresses.hasOwnProperty(business.address_id)) {
							business.address = addresses[business.address_id];
						} else {
							business.address = null;
						}
						bObj[business.business_id] = business;
					});
					businessesData.users.forEach(user => {
						if(bObj.hasOwnProperty(user.business_id)) {
							bObj[user.business_id].users[user.user_id] = user;
						}
					});
					businessesData.feerules.forEach(rule => {
						if(bObj.hasOwnProperty(rule.business_id)) {
							let ruleData = {
								active: false,
								fee_rule_id: Number(rule.fee_rule_id),
								business_id: Number(rule.business_id),
								fee_type: rule.fee_type,
								min_limit: Number(rule.min_limit),
								max_limit: rule.max_limit ? Number(rule.max_limit) : null,
								fixed: Number(rule.fixed),
								percent: Number(rule.percent),
								premium_percent: Number(rule.premium_percent),
							};
							if (rule.fee_type === "standard") {
								ruleData.alternative_fixed = Number(rule.alternative_fixed);
								ruleData.alternative_percent = Number(rule.alternative_percent);
							}
							if(bObj[rule.business_id].feerules.hasOwnProperty(rule.fee_type)) {
								bObj[rule.business_id].feerules[rule.fee_type].push(ruleData);
							} else {
								bObj[rule.business_id].feerules[rule.fee_type] = [ruleData];
							}
						}
					});
					let sortedBusinesses = Object.values(bObj).sort((a, b) => a.name.localeCompare(b.name));
					sortedBusinesses.forEach(business => {
						business.users = Object.values(business.users).sort((a, b) => a.email.localeCompare(b.email));
					});
					this.businesses = sortedBusinesses;
					this.isSuper = !!businessesData.is_super;
					this.isUltra = !!businessesData.is_ultra;
					this.isMega = !!businessesData.is_mega;
				}
				if(callback) { callback(); }
			});
		},
		forceLogin(userId, businessId, callback = null) {

			API.init().superLogin(userId, businessId, r => {

				if(r && r.token) {
					sessionStorage.setItem("oauth_token", r.token);
					localStorage.removeItem("refresh_token");
					callback && callback("success", r.token);
				} else {
					callback && callback(r.details || "error", null);
				}

			});
		},
		changeBusinessType(businessId, type, callback = null) {

			API.init().superUpdateBusinessType(businessId, type, function (r) {
				if(r && r.result && r.result === "OK") {
					callback && callback(true);
				} else {
					callback && callback(false);
				}
			});

		},
		changeTransferPrefix(businessId, prefix, callback = null) {

			API.init().superUpdateBusinessTransferPrefix(businessId, prefix, function (r) {
				if(r && r.result && r.result === "OK") {
					callback && callback(true);
				} else {
					callback && callback(false);
				}
			});

		},
		updateTapToPayFlag(businessId, flag, callback = null) {

			API.init().superUpdateTapToPayFlag(businessId, flag, function (r) {
				if (r && r.result && r.result === "OK") {
					callback && callback(true);
				} else {
					callback && callback(false);
				}
			});

		},
		changeBusinessCredits(businessId, credits, repaymentRate, callback = null) {

			API.init().superUpdateBusinessCredits(businessId, credits, repaymentRate, function (r) {
				if(r && r.result && r.result === "OK") {
					callback && callback("success");
				} else if(r && r.result && r.result === "KO" && r.details && r.details === "ultra") {
					callback && callback("ultra");
				} else {
					callback && callback(false);
				}
			});

		},
		changeFeeRules(businessId, type, rules, callback = null) {
			const self = this;
			API.init().superUpdateBusinessFeeRules(businessId, type, rules, function (r) {
				if(r && r.result && r.result === "OK") {
					callback && callback("success");
				} else {
					if(r && r.min_allowed !== undefined) {
						self.minFeePerAllowed = r.min_allowed;
					}
					callback && callback(r.details || "error");
				}
			});
		},
		savePayoutInterval(businessId, interval, callback = null) {
			API.init().superUpdateBusinessPayoutInterval(businessId, interval, function (r) {
				if(r && r.result && r.result === "OK") {
					callback && callback(true);
				} else {
					callback && callback(false);
				}
			});
		},
		savePayoutDelay(businessId, delay, callback = null) {
			API.init().superUpdateBusinessPayoutDelay(businessId, delay, function (r) {
				if(r && r.result && r.result === "OK") {
					callback && callback(true);
				} else {
					callback && callback(false);
				}
			});
		},
		reportCommissions(businessId, startDate, endDate, callback = null) {
			API.init().superReportCommissions(businessId, startDate, endDate, function (r) {
				if(r && r.result && r.result === "OK" && r.data) {
					callback && callback(r.data);
				} else {
					callback && callback(false);
				}
			});
		},
	},
});
